import { useEffect, useState } from 'react';
import { CpmButton, CpmModal } from '../../../../components/design-system';
import { formatCpmCount } from '../../../../constants/centralProductMaster.constants';
import type { CpmState } from '../../../../types/centralProductMaster.types';

interface MarkNotLiveModalProps {
    open: boolean;
    state: CpmState | null;
    categoryCount: number;
    onClose: () => void;
    onConfirm: () => Promise<void>;
    confirming?: boolean;
}

type Phase = 'confirm' | 'saveError';

export default function MarkNotLiveModal({
    open,
    state,
    categoryCount,
    onClose,
    onConfirm,
    confirming = false,
}: MarkNotLiveModalProps) {
    const [phase, setPhase] = useState<Phase>('confirm');
    const [localBusy, setLocalBusy] = useState(false);

    useEffect(() => {
        if (open) {
            setPhase('confirm');
            setLocalBusy(false);
        }
    }, [open, state?.name]);

    if (!state) return null;

    const operating = (state.activeStoreCount || 0) + (state.trialStoreCount || 0);
    const blocked = operating > 0;
    const working = confirming || localBusy;
    const storeRows: Array<{ n: string; label: string }> = [];
    if (state.activeStoreCount > 0) {
        storeRows.push({
            n: formatCpmCount(state.activeStoreCount),
            label: `stores actively operating in ${state.name}`,
        });
    }
    if (state.trialStoreCount > 0) {
        storeRows.push({
            n: formatCpmCount(state.trialStoreCount),
            label: `stores in trial in ${state.name}`,
        });
    }

    const title = blocked ? `Cannot mark ${state.name} not live` : `Mark ${state.name} not live?`;
    const body = blocked
        ? `${state.name} still has stores operating, so its linked variants keep needing state-product rows — which only get created while this state is live and curated. The flag can't contradict that.`
        : `No stores are active or in trial in ${state.name}. It moves out of the live list, and state-product creation for its linked variants stops until it is live again. ${
              categoryCount === 0
                  ? `Nothing has been curated for ${state.name} yet — mark it live again any time.`
                  : `Its ${formatCpmCount(categoryCount)} curated ${categoryCount === 1 ? 'category stays' : 'categories stay'} saved — mark it live again any time.`
          }`;

    const runConfirm = async () => {
        setLocalBusy(true);
        try {
            await onConfirm();
            onClose();
        } catch {
            setPhase('saveError');
        } finally {
            setLocalBusy(false);
        }
    };

    const finish = () => {
        if (working) return;
        setPhase('confirm');
        onClose();
    };

    return (
        <CpmModal open={open} onClose={finish} maxWidth={540} zIndex={74}>
            {phase === 'saveError' ? (
                <>
                    <div
                        className="flex items-start"
                        style={{ gap: 14, padding: '22px 24px 16px' }}
                    >
                        <span
                            className="shrink-0 inline-flex items-center justify-center text-[var(--cpm-shortfall-text)]"
                            style={{
                                width: 38,
                                height: 38,
                                borderRadius: 10,
                                background: 'var(--cpm-shortfall-bg)',
                            }}
                        >
                            <svg
                                width="19"
                                height="19"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                <path d="M12 9v4" />
                                <path d="M12 17h.01" />
                            </svg>
                        </span>
                        <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 6 }}>
                            <h2 className="m-0 !text-[19px] !font-extrabold !tracking-[-0.02em] !leading-[1.2] !text-[var(--cpm-brand-navy)]">
                                Couldn&apos;t update the live flag
                            </h2>
                            <p className="m-0 !text-[13px] !font-semibold !leading-[1.5] !text-[var(--cpm-text-secondary)]">
                                The catalog service did not accept the change for {state.name}. The
                                state is still live. Try again.
                            </p>
                        </div>
                    </div>
                    <div
                        className="flex items-center border-t-[1.5px] border-[var(--cpm-border-strong)]"
                        style={{
                            gap: 10,
                            padding: '18px 24px',
                            background: 'rgba(249,249,252,0.88)',
                        }}
                    >
                        <div className="ml-auto flex items-center" style={{ gap: 10 }}>
                            <CpmButton variant="outline" size="modal" onClick={finish}>
                                Close
                            </CpmButton>
                            <CpmButton
                                variant="navy"
                                size="modal"
                                isLoading={working}
                                onClick={() => void runConfirm()}
                            >
                                Try again
                            </CpmButton>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div
                        className="flex items-start"
                        style={{ gap: 14, padding: '22px 24px 16px' }}
                    >
                        <span
                            className="shrink-0 inline-flex items-center justify-center"
                            style={{
                                width: 38,
                                height: 38,
                                borderRadius: 10,
                                background: blocked
                                    ? 'var(--cpm-warning-bg)'
                                    : 'var(--cpm-purple-tint-13)',
                                color: blocked
                                    ? 'var(--cpm-warning-text)'
                                    : 'var(--cpm-brand-purple)',
                            }}
                        >
                            {blocked ? (
                                <svg
                                    width="19"
                                    height="19"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                >
                                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                    <path d="M12 9v4" />
                                    <path d="M12 17h.01" />
                                </svg>
                            ) : (
                                <svg
                                    width="19"
                                    height="19"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                >
                                    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                                    <path d="M4 22v-7" />
                                </svg>
                            )}
                        </span>
                        <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 6 }}>
                            <h2 className="m-0 !text-[19px] !font-extrabold !tracking-[-0.02em] !leading-[1.2] !text-[var(--cpm-brand-navy)]">
                                {title}
                            </h2>
                            <p className="m-0 !text-[13px] !font-semibold !leading-[1.5] !text-[var(--cpm-text-secondary)]">
                                {body}
                            </p>
                        </div>
                    </div>

                    {blocked ? (
                        <div className="flex flex-col" style={{ margin: '0 24px 4px', gap: 8 }}>
                            <span
                                className="uppercase"
                                style={{
                                    fontSize: 10.5,
                                    fontWeight: 800,
                                    letterSpacing: '0.08em',
                                    color: 'var(--cpm-text-tertiary)',
                                }}
                            >
                                Still operating
                            </span>
                            <div className="flex flex-col" style={{ gap: 6 }}>
                                {storeRows.map((r) => (
                                    <div
                                        key={r.label}
                                        className="flex items-center rounded-[10px] bg-[var(--cpm-warning-bg)] border border-[var(--cpm-warning-border)]"
                                        style={{ gap: 12, padding: '11px 14px' }}
                                    >
                                        <span
                                            className="tabular text-[var(--cpm-warning-text)]"
                                            style={{ fontSize: 16, fontWeight: 800, minWidth: 44 }}
                                        >
                                            {r.n}
                                        </span>
                                        <span
                                            className="flex-1 text-[var(--cpm-warning-text)]"
                                            style={{ fontSize: 12.5, fontWeight: 700 }}
                                        >
                                            {r.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <span
                                style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    lineHeight: 1.5,
                                    color: 'var(--cpm-text-secondary)',
                                }}
                            >
                                Close or hand over those stores first. Until then the flag has to
                                stay live — variants linked in operating stores still need their
                                state-product rows created.
                            </span>
                        </div>
                    ) : null}

                    <div
                        className="flex items-center border-t-[1.5px] border-[var(--cpm-border-strong)]"
                        style={{
                            gap: 10,
                            padding: '18px 24px',
                            marginTop: 14,
                            background: 'rgba(249,249,252,0.88)',
                        }}
                    >
                        <div className="ml-auto flex items-center" style={{ gap: 10 }}>
                            <CpmButton
                                variant="outline"
                                size="modal"
                                onClick={finish}
                                disabled={working}
                            >
                                {blocked ? 'Keep it live' : 'Cancel'}
                            </CpmButton>
                            {!blocked ? (
                                <CpmButton
                                    variant="navy"
                                    size="modal"
                                    onClick={() => void runConfirm()}
                                    isLoading={working}
                                >
                                    Mark not live
                                </CpmButton>
                            ) : null}
                        </div>
                    </div>
                </>
            )}
        </CpmModal>
    );
}
